import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiBookmark,
  FiSearch,
  FiFilter,
  FiDownload,
  FiX,
  FiChevronDown,
  FiClock,
} from "react-icons/fi";
import PaperCard from "./PaperCard";
import "./styles/SavedPapers.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const toolbarVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

const SavedPapers = ({
  savedPapers = [],
  onExport,
  loading = false,
  collections = [],
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("savedAt");
  const [filterCollection, setFilterCollection] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Filter and sort papers
  const filteredPapers = useMemo(() => {
    let papers = [...savedPapers];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      papers = papers.filter(
        (paper) =>
          paper.title?.toLowerCase().includes(query) ||
          paper.authors?.some((a) =>
            typeof a === "string"
              ? a.toLowerCase().includes(query)
              : a.name?.toLowerCase().includes(query)
          ) ||
          paper.topics?.some((t) => t.toLowerCase().includes(query))
      );
    }

    // Collection filter
    if (filterCollection !== "all") {
      papers = papers.filter(
        (paper) => paper.collectionId === filterCollection
      );
    }

    // Sort
    papers.sort((a, b) => {
      switch (sortBy) {
        case "savedAt":
          return new Date(b.savedAt) - new Date(a.savedAt);
        case "title":
          return (a.title || "").localeCompare(b.title || "");
        case "year":
          return (
            (b.year || b.publicationYear || 0) -
            (a.year || a.publicationYear || 0)
          );
        case "citations":
          return (b.citationCount || 0) - (a.citationCount || 0);
        default:
          return 0;
      }
    });

    return papers;
  }, [savedPapers, searchQuery, filterCollection, sortBy]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="exp-saved-container">
      {/* Header */}
      <motion.div
        className="exp-saved-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="exp-saved-header-content">
          <div className="exp-saved-title-section">
            <div className="exp-saved-icon">
              <FiBookmark />
            </div>
            <div>
              <h1 className="exp-saved-title">Saved Papers</h1>
              <p className="exp-saved-subtitle">
                {savedPapers.length} paper{savedPapers.length !== 1 ? "s" : ""}{" "}
                in your library
              </p>
            </div>
          </div>

          <div className="exp-saved-header-actions">
            <motion.button
              className="exp-saved-export-btn"
              onClick={() => onExport && onExport(savedPapers, "bibtex")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={savedPapers.length === 0}
            >
              <FiDownload />
              <span>Export All</span>
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Toolbar */}
      <motion.div
        className="exp-saved-toolbar"
        variants={toolbarVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="exp-saved-toolbar-left">
          {/* Search */}
          <div className="exp-saved-search">
            <FiSearch className="exp-saved-search-icon" />
            <input
              type="text"
              placeholder="Search saved papers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="exp-saved-search-input"
            />
            {searchQuery && (
              <button
                className="exp-saved-search-clear"
                onClick={() => setSearchQuery("")}
              >
                <FiX />
              </button>
            )}
          </div>

          {/* Filter toggle */}
          <motion.button
            className={`exp-saved-filter-btn ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiFilter />
            <span>Filters</span>
            <FiChevronDown
              className={`exp-saved-filter-chevron ${
                showFilters ? "rotated" : ""
              }`}
            />
          </motion.button>
        </div>

        <div className="exp-saved-toolbar-right">
          {/* Sort dropdown */}
          <div className="exp-saved-sort">
            <label>Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="exp-saved-sort-select"
            >
              <option value="savedAt">Date Saved</option>
              <option value="title">Title</option>
              <option value="year">Year</option>
              <option value="citations">Citations</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Filters panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            className="exp-saved-filters-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="exp-saved-filter-group">
              <label>Collection</label>
              <select
                value={filterCollection}
                onChange={(e) => setFilterCollection(e.target.value)}
                className="exp-saved-filter-select"
              >
                <option value="all">All Collections</option>
                {collections.map((col) => (
                  <option key={col.id} value={col.id}>
                    {col.name}
                  </option>
                ))}
              </select>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Papers list - Using PaperCard component */}
      {loading ? (
        <div className="exp-saved-loading">
          <div className="exp-saved-loading-spinner" />
          <p>Loading your saved papers...</p>
        </div>
      ) : filteredPapers.length === 0 ? (
        <motion.div
          className="exp-saved-empty"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="exp-saved-empty-icon">
            {searchQuery ? <FiSearch /> : <FiBookmark />}
          </div>
          <h3>{searchQuery ? "No matching papers" : "No saved papers yet"}</h3>
          <p>
            {searchQuery
              ? "Try adjusting your search query"
              : "Papers you save will appear here for easy access"}
          </p>
        </motion.div>
      ) : (
        <motion.div
          className="exp-saved-papers-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredPapers.map((paper) => (
            <motion.div
              key={paper.id || paper._id || paper.paperId}
              className="exp-saved-paper-wrapper"
              variants={itemVariants}
              layout
            >
              {/* Saved date indicator */}
              {paper.savedAt && (
                <div className="exp-saved-date-badge">
                  <FiClock />
                  <span>Saved {formatDate(paper.savedAt)}</span>
                </div>
              )}
              {/* Use the same PaperCard component for consistent look */}
              <PaperCard paper={paper} />
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default SavedPapers;
